import type { Locale, ProjectStatus, TechCategory } from '@/lib/types';

const es = {
  nav: {
    home: 'Inicio',
    projects: 'Proyectos',
    about: 'Sobre mí',
    certificates: 'Certificados',
    contact: 'Contacto',
    menu: 'Menú',
    close: 'Cerrar',
  },
  hero: {
    available: 'Disponible para trabajar',
    freelance: 'Disponible para proyectos freelance',
    notLooking: 'No disponible actualmente',
    viewProjects: 'Ver proyectos',
    downloadCv: 'Descargar CV',
    getInTouch: 'Contactar',
    scroll: 'Desliza',
  },
  sections: {
    selectedWork: 'Trabajo seleccionado',
    selectedWorkTitle: 'Cosas que construí y que hoy usa alguien',
    allProjects: 'Todos los proyectos',
    minorWork: 'Prácticas y ejercicios',
    minorWorkIntro:
      'Proyectos pequeños de universidad y experimentos. Los dejo visibles porque muestran de dónde vengo.',
    skills: 'Stack',
    skillsTitle: 'Tecnologías con las que trabajo',
    skillsIntro:
      'Solo aparece lo que he usado en proyectos reales. El número al lado indica en cuántos.',
    about: 'Sobre mí',
    experience: 'Experiencia',
    education: 'Formación',
    certificates: 'Certificados',
    certificatesIntro: 'Formación complementaria y credenciales verificables.',
    contact: 'Contacto',
    contactTitle: 'Escríbeme',
    contactIntro:
      'Una vacante, un proyecto o simplemente una duda sobre algo que viste aquí. Respondo el mismo día.',
  },
  footer: {
    cta: 'Disponible',
    // Direct beats clever here: stating the ask plainly reads as confidence
    // when the work above it already made the case.
    headline: 'Busco mi primer puesto como desarrollador.',
    copy: 'Copiar correo',
    copied: 'Copiado',
    builtWith: 'React · TypeScript · Express · PostgreSQL · Docker',
  },
  skills: {
    level: 'Nivel',
    levelLegend: 'Nivel de dominio, de 1 a 10',
    countLegend: '×N = proyectos donde lo he usado',
    strongest: 'Lo que más manejo',
    totalSuffix: 'tecnologías',
  },
  project: {
    role: 'Rol',
    period: 'Periodo',
    stack: 'Stack',
    highlights: 'Puntos clave',
    overview: 'Descripción',
    components: 'Qué incluye',
    gallery: 'Galería',
    viewCode: 'Ver código',
    viewLive: 'Visitar sitio',
    privateRepo: 'Código privado',
    back: 'Volver a proyectos',
    present: 'Actualidad',
    next: 'Siguiente proyecto',
    filterAll: 'Todos',
    filterBy: 'Filtrar por tecnología',
    noResults: 'No hay proyectos con esa tecnología.',
    notFound: 'No encontré ese proyecto.',
  },
  status: {
    IN_PRODUCTION: 'En producción',
    IN_PROGRESS: 'En desarrollo',
    COMPLETED: 'Completado',
    ARCHIVED: 'Archivado',
  } satisfies Record<ProjectStatus, string>,
  category: {
    LANGUAGE: 'Lenguajes',
    FRONTEND: 'Frontend',
    BACKEND: 'Backend',
    DATABASE: 'Bases de datos',
    INFRASTRUCTURE: 'Infraestructura',
    TOOL: 'Herramientas',
    PRACTICE: 'Prácticas',
  } satisfies Record<TechCategory, string>,
  contact: {
    name: 'Nombre',
    namePlaceholder: 'Cómo te llamas',
    email: 'Correo',
    emailPlaceholder: 'tu@correo.com',
    subject: 'Asunto',
    subjectPlaceholder: 'Sobre qué quieres hablar',
    message: 'Mensaje',
    messagePlaceholder: 'Cuéntame un poco más…',
    send: 'Enviar mensaje',
    sending: 'Enviando…',
    success: 'Mensaje enviado. Te respondo pronto.',
    error: 'No se pudo enviar el mensaje.',
    orEmail: 'O escríbeme directamente a',
  },
  certificate: {
    issued: 'Emitido',
    expires: 'Expira',
    credential: 'Credencial',
    verify: 'Verificar',
    empty: 'Aún no hay certificados publicados.',
  },
  common: {
    loading: 'Cargando…',
    error: 'Algo salió mal.',
    retry: 'Reintentar',
    notFound: 'Página no encontrada',
    notFoundText: 'La página que buscas no existe o cambió de dirección.',
    goHome: 'Volver al inicio',
    theme: 'Cambiar tema',
    language: 'Cambiar idioma',
    projects: 'proyectos',
    project: 'proyecto',
    usedIn: 'proyectos',
    yearsUsing: 'Desde',
    location: 'Ubicación',
    rightsReserved: 'Todos los derechos reservados.',
    builtWith: 'Construido con',
  },
};

const en: typeof es = {
  nav: {
    home: 'Home',
    projects: 'Projects',
    about: 'About',
    certificates: 'Certificates',
    contact: 'Contact',
    menu: 'Menu',
    close: 'Close',
  },
  hero: {
    available: 'Open to work',
    freelance: 'Available for freelance work',
    notLooking: 'Not available right now',
    viewProjects: 'View projects',
    downloadCv: 'Download CV',
    getInTouch: 'Get in touch',
    scroll: 'Scroll',
  },
  sections: {
    selectedWork: 'Selected work',
    selectedWorkTitle: 'Things I built that someone actually uses',
    allProjects: 'All projects',
    minorWork: 'Coursework and experiments',
    minorWorkIntro:
      'Small university projects and experiments. I keep them visible because they show where I started.',
    skills: 'Stack',
    skillsTitle: 'Technologies I work with',
    skillsIntro:
      'Only what I have used in real projects appears here. The number shows how many.',
    about: 'About',
    experience: 'Experience',
    education: 'Education',
    certificates: 'Certificates',
    certificatesIntro: 'Complementary training and verifiable credentials.',
    contact: 'Contact',
    contactTitle: 'Get in touch',
    contactIntro:
      'A role, a project, or just a question about something you saw here. I reply the same day.',
  },
  footer: {
    cta: 'Available',
    headline: "I'm looking for my first developer role.",
    copy: 'Copy email',
    copied: 'Copied',
    builtWith: 'React · TypeScript · Express · PostgreSQL · Docker',
  },
  skills: {
    level: 'Level',
    levelLegend: 'Proficiency, 1 to 10',
    countLegend: '×N = projects where I used it',
    strongest: 'Strongest with',
    totalSuffix: 'technologies',
  },
  project: {
    role: 'Role',
    period: 'Period',
    stack: 'Stack',
    highlights: 'Highlights',
    overview: 'Overview',
    components: "What's inside",
    gallery: 'Gallery',
    viewCode: 'View code',
    viewLive: 'Visit site',
    privateRepo: 'Private source',
    back: 'Back to projects',
    present: 'Present',
    next: 'Next project',
    filterAll: 'All',
    filterBy: 'Filter by technology',
    noResults: 'No projects use that technology.',
    notFound: 'I could not find that project.',
  },
  status: {
    IN_PRODUCTION: 'In production',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    ARCHIVED: 'Archived',
  },
  category: {
    LANGUAGE: 'Languages',
    FRONTEND: 'Frontend',
    BACKEND: 'Backend',
    DATABASE: 'Databases',
    INFRASTRUCTURE: 'Infrastructure',
    TOOL: 'Tooling',
    PRACTICE: 'Practices',
  },
  contact: {
    name: 'Name',
    namePlaceholder: 'Your name',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    subject: 'Subject',
    subjectPlaceholder: 'What would you like to discuss',
    message: 'Message',
    messagePlaceholder: 'Tell me a little more…',
    send: 'Send message',
    sending: 'Sending…',
    success: 'Message sent. I will get back to you soon.',
    error: 'The message could not be sent.',
    orEmail: 'Or email me directly at',
  },
  certificate: {
    issued: 'Issued',
    expires: 'Expires',
    credential: 'Credential',
    verify: 'Verify',
    empty: 'No certificates published yet.',
  },
  common: {
    loading: 'Loading…',
    error: 'Something went wrong.',
    retry: 'Retry',
    notFound: 'Page not found',
    notFoundText: 'The page you are looking for does not exist or has moved.',
    goHome: 'Back to home',
    theme: 'Toggle theme',
    language: 'Switch language',
    projects: 'projects',
    project: 'project',
    usedIn: 'projects',
    yearsUsing: 'Since',
    location: 'Location',
    rightsReserved: 'All rights reserved.',
    builtWith: 'Built with',
  },
};

export const translations = { es, en } satisfies Record<Locale, typeof es>;

export type Translation = typeof es;
